/**
 * Barcode input with scanning, for the Admin product forms.
 *
 * Previously the scan icon either focused the field (New Product) or did
 * nothing at all (Edit Product), so there was no way to scan a barcode into a
 * product — unlike POS quick-add. This wires the icon to both scan paths:
 *
 *  - USB / Bluetooth keyboard-wedge scanner — the common Admin desktop setup.
 *    The reader types the code into the focused field, so "scan" means focus.
 *  - Device camera — for laptops, tablets and phones, via html5-qrcode.
 *
 * When a camera is available the icon opens a small chooser; otherwise it goes
 * straight to focusing the field, so desktop users see no pointless prompt.
 */

import { useEffect, useRef, useState } from 'react';
import { Input } from '@bbpos/ui';
import { cn } from '@/constants/design-tokens';
import { focusBarcodeInput } from '@/pages/product-barcode-scan';
import { isCameraScanSupported, normalizeScannedBarcode } from './barcode-scan-logic';
import { BarcodeScannerModal } from './BarcodeScannerModal';

function CameraIcon() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.5 4h-5L7 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  );
}

function WedgeIcon() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
      <path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14M21 5v14" />
    </svg>
  );
}

export function BarcodeScanField({
  id,
  value,
  onChange,
  placeholder = 'Scan or enter barcode',
  error,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(false);

  // Capability probe runs on the client only — `navigator` and
  // `isSecureContext` are unavailable during any non-browser render.
  useEffect(() => {
    setCameraSupported(
      isCameraScanSupported(
        typeof navigator === 'undefined' ? undefined : navigator,
        typeof window === 'undefined' ? false : window.isSecureContext,
      ),
    );
  }, []);

  // Dismiss the chooser on an outside click.
  useEffect(() => {
    if (!chooserOpen) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setChooserOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [chooserOpen]);

  function handleScanClick() {
    // No camera (the usual desktop case) — go straight to the wedge path
    // rather than showing a chooser with a single option.
    if (!cameraSupported) {
      focusBarcodeInput(inputRef.current);
      return;
    }
    setChooserOpen((o) => !o);
  }

  function useWedge() {
    setChooserOpen(false);
    focusBarcodeInput(inputRef.current);
  }

  function useCamera() {
    setChooserOpen(false);
    setCameraOpen(true);
  }

  return (
    <div ref={wrapRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        variant="barcode"
        placeholder={placeholder}
        value={value}
        error={error}
        // A keyboard-wedge scanner types the code then sends Enter/CR; strip
        // the stray control characters before they reach the form state.
        onChange={(v) => onChange(normalizeScannedBarcode(v))}
        onScan={handleScanClick}
      />

      {chooserOpen && (
        <ul className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-lg border border-neutral-200 bg-white py-1 shadow-lg animate-fade-in dark:border-neutral-700 dark:bg-neutral-800">
          <li>
            <button
              type="button"
              onClick={useCamera}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                'text-neutral-700 hover:bg-brand-50 dark:text-neutral-200 dark:hover:bg-neutral-700',
              )}
            >
              <CameraIcon />
              <span>Scan with camera</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={useWedge}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                'text-neutral-700 hover:bg-brand-50 dark:text-neutral-200 dark:hover:bg-neutral-700',
              )}
            >
              <WedgeIcon />
              <span>Use USB scanner</span>
            </button>
          </li>
        </ul>
      )}

      <BarcodeScannerModal
        open={cameraOpen}
        onScan={(code) => onChange(code)}
        onClose={() => setCameraOpen(false)}
      />
    </div>
  );
}
